'use client';

import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  ASSETS_CSV_FIELD_KEYS,
  ASSETS_CSV_HEADERS,
  exportToCSV,
  mapAssetExportRowToCsvRow,
  toasterApi,
} from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  buildVerifiedAssetsSearchBody,
  type AssetsSearchRequestFilters,
} from './assets-search-request';

const EXPORT_REQUEST_LIMIT = 200;

export function useAssetsCsvExport({
  applicationId,
  filters,
}: {
  applicationId: string | null | undefined;
  filters: AssetsSearchRequestFilters;
}) {
  const t = useTranslations('proxy.assets.export');
  const body = buildVerifiedAssetsSearchBody({
    filters: { ...filters, groupBy: undefined },
    skip: 0,
    limit: EXPORT_REQUEST_LIMIT,
  });
  const mutation = useMutation({
    mutationFn: async () => {
      if (!applicationId || !body) {
        throw new Error('Assets export is unavailable for the current view');
      }

      return terraProxyApiClient.exportApplicationAssets({
        applicationId,
        body,
      });
    },
    onSuccess: ({ assets, truncated }) => {
      if (assets.length === 0) {
        toasterApi.create({ type: 'warning', title: t('empty') });

        return;
      }

      const date = new Date().toISOString().split('T')[0];

      exportToCSV(
        assets.map(mapAssetExportRowToCsvRow),
        ASSETS_CSV_HEADERS,
        ASSETS_CSV_FIELD_KEYS,
        `assets-${applicationId}-${body?.type ?? 'endpoints'}-${date}.csv`,
      );

      if (truncated) {
        toasterApi.create({ type: 'warning', title: t('truncated') });

        return;
      }

      toasterApi.success({ title: t('success') });
    },
    onError: (error: unknown) => {
      console.warn('Assets CSV export failed', {
        error: error instanceof Error ? error : new Error(String(error)),
        props: { applicationId, type: body?.type },
      });
      toasterApi.error({ title: t('failed') });
    },
  });

  return {
    exportAssets: mutation.mutate,
    isExporting: mutation.isPending,
    canExport: Boolean(applicationId && body),
  };
}
